import PhoneField from '@components/v2/form/PhoneField/PhoneField';
import TextField from '@components/v2/form/TextField/TextField';
import DiscardChangesModal from '@components/v2/ui/DiscardChangesModal/DiscardChangesModal';
import Modal from '@components/v2/ui/Modal/Modal';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Grid } from '@mui/material';
import { useId } from 'react';
import { Controller } from 'react-hook-form';
import LangEnum from '../../../lang/LangEnum';
import { SignatureInfo } from '../../../lib/SiteLib';
import { useTranslation } from '../../../modules/translation/TranslationContext';
import usePersonSignatureForm from './hooks/usePersonSignatureForm';

type PersonSignatureFormProps = {
  isOpen: boolean;
  initialValues?: SignatureInfo;
  onSave: (values: SignatureInfo) => Promise<void>;
  onClose: () => void;
};

const PersonSignatureForm = ({
  isOpen,
  initialValues,
  onSave,
  onClose,
}: PersonSignatureFormProps) => {
  const { translate } = useTranslation();
  const formId = useId();

  const { control, setValue, isSaving, discardModalProps, save, handleExited, handleRequestClose } =
    usePersonSignatureForm({
      initialValues,
      onSave,
      onClose,
    });

  return (
    <>
      <Modal
        isOpen={isOpen}
        size="standard"
        header={{ title: LangEnum.SHOWROOM_PERSON_SIGNATURE_TITLE }}
        onClose={handleRequestClose}
        onExited={handleExited}
        footer={{
          primaryAction: {
            label: LangEnum.GLOBAL_SAVE,
            isLoading: isSaving,
            type: 'submit',
            form: formId,
          },
          secondaryAction: {
            label: LangEnum.GLOBAL_CANCEL_LABEL,
            onClick: handleRequestClose,
          },
        }}
      >
        <Box component="form" id={formId} noValidate onSubmit={save}>
          <Grid container spacing={2} sx={{ pt: 1.5 }}>
            <Grid size={12}>
              <Typography variant="h4" color="text.secondary">
                {initialValues?.name}
              </Typography>
            </Grid>

            <Grid size={12}>
              <Controller
                name="role"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    autoFocus
                    label={translate(LangEnum.GLOBAL_DEPARTMENT)}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={translate(LangEnum.GLOBAL_EMAIL)}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="phone"
                control={control}
                render={({ field, fieldState }) => (
                  <PhoneField
                    name={field.name}
                    value={field.value}
                    label={translate(LangEnum.GLOBAL_TELEPHONE)}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    onBlur={field.onBlur}
                    onChange={(phone, data) => {
                      field.onChange(phone);
                      setValue('countryCode', data.dialCode);
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </Modal>

      <DiscardChangesModal {...discardModalProps} />
    </>
  );
};

export default PersonSignatureForm;
